import React from 'react';
import { Box, Theme, useMediaQuery } from '@mui/material';

interface Props {
  src: string;
  fallbackSrc?: string;
  isFullWidth?: boolean;
  keepPrevious?: boolean;
  gradient?: boolean;
  isOffsetRight?: boolean;
  imageStyle?: React.CSSProperties;
}

function Image({
  src,
  style,
  onError,
}: {
  src: string;
  style?: React.CSSProperties;
  onError?: () => void;
}) {
  return (
    <img
      src={src}
      alt="step flow"
      style={{
        ...style,
      }}
      onError={onError}
    />
  );
}

function StepFlowImage({
  src,
  fallbackSrc,
  isFullWidth,
  gradient,
  imageStyle,
  isOffsetRight,
  keepPrevious = false,
}: Props) {
  const isMediumOrLarger = useMediaQuery<Theme>(theme =>
    theme.breakpoints.up('md'),
  );

  const showGradient = gradient && isMediumOrLarger;
  const showOffsetRight = isOffsetRight && isMediumOrLarger;

  // prevent image flickering when changing src - or rendering the previous image while the new one is loading
  const [previousSrcList, setPreviousSrcList] = React.useState<Set<string>>(
    new Set(),
  );
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    setIsError(false);
  }, [src]);

  React.useEffect(() => {
    setPreviousSrcList(prev => {
      const newSet = new Set(prev);
      newSet.add(src);
      return newSet;
    });
  }, [src]);

  const imgStyle: React.CSSProperties = {
    marginLeft: showOffsetRight ? '10%' : undefined,
    width: showOffsetRight ? 'calc(100% + 20%)' : '100%',
    ...imageStyle,
  };

  return (
    <Box
      width="100%"
      maxWidth={{
        xs: isFullWidth ? '100%' : '75%',
        sm: '50%',
        md: isFullWidth ? '100%' : '350px',
      }}
      mx="auto"
      position="relative">
      {keepPrevious &&
        Array.from(previousSrcList).map(previousSrc => {
          const isActive = src === previousSrc;
          const useFallback = isActive && isError && fallbackSrc;
          return (
            <Image
              key={previousSrc}
              src={useFallback ? fallbackSrc! : previousSrc}
              style={{
                ...imgStyle,
                display: isActive ? 'block' : 'none',
              }}
              onError={isActive ? () => setIsError(true) : undefined}
            />
          );
        })}
      {!keepPrevious && (
        <Image
          src={isError && fallbackSrc ? fallbackSrc : src}
          style={imgStyle}
          onError={() => setIsError(true)}
        />
      )}
      {showGradient && (
        <Box
          position="absolute"
          width="85%"
          height="100%"
          top={0}
          bottom={0}
          right={0}
          style={{
            background:
              'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}

export default StepFlowImage;
